// Google Analytics 4 tracking utilities for demo requests
// Epoch 32: Demo Booking & Contact Forms

declare global {
  interface Window {
    gtag?: (command: string, action: string, parameters?: GAEventParams) => void;
    dataLayer?: Array<Record<string, unknown>>;
  }
}

interface GAEventParams {
  event_category?: string;
  event_label?: string;
  value?: number;
  currency?: string;
  [key: string]: string | number | boolean | undefined;
}

export const useGoogleAnalytics = () => {
  // Track custom events
  const trackEvent = (action: string, parameters?: GAEventParams) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', action, parameters);
    } else {
      console.log('GA Event:', action, parameters); // Debug logging
    }
  };

  // Track demo-specific events
  const trackDemoEvent = (action: string, additionalParams?: Record<string, string | number | boolean | undefined>) => {
    trackEvent(action, {
      event_category: 'Lead Generation',
      ...additionalParams
    });
  };

  // Get Google Analytics client ID from cookies
  const getGAClientId = (): string | null => {
    try {
      // GA4 stores client ID in _ga cookie
      const gaCookie = document.cookie
        .split('; ')
        .find(row => row.startsWith('_ga='));
      
      if (gaCookie) {
        // Format: _ga=GA1.1.XXXXXXXXX.YYYYYYYYY
        const parts = gaCookie.split('.');
        if (parts.length >= 4) {
          return `${parts[2]}.${parts[3]}`;
        }
      }
    } catch (error) {
      console.error('Error getting GA client ID:', error);
    }
    return null;
  };

  // Get GA session ID
  const getGASessionId = (): string | null => {
    try {
      // GA4 stores session info in _ga_<MEASUREMENT_ID> cookie
      // For now, we'll use a timestamp-based session ID
      const sessionKey = 'ga_session_id';
      let sessionId = sessionStorage.getItem(sessionKey);
      
      if (!sessionId) {
        sessionId = Date.now().toString();
        sessionStorage.setItem(sessionKey, sessionId);
      }
      
      return sessionId;
    } catch (error) {
      console.error('Error getting GA session ID:', error);
    }
    return null;
  };

  // Extract UTM parameters from URL
  const getUTMParams = () => {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_term: params.get('utm_term'),
      utm_content: params.get('utm_content')
    };
  };

  // Track modal open
  const trackModalOpen = (modalType: 'demo' | 'contact_sales', sourceButton?: string) => {
    trackDemoEvent('view_modal', {
      modal_type: modalType,
      source_page: window.location.pathname,
      source_button: sourceButton,
      event_label: modalType === 'demo' ? 'Book Demo Modal' : 'Contact Sales Modal'
    });
  };

  // Track form start (first interaction)
  const trackFormStart = (formType: 'demo' | 'contact_sales') => {
    trackDemoEvent('form_start', {
      form_type: formType,
      event_label: formType === 'demo' ? 'Demo Form Start' : 'Sales Form Start'
    });
  };

  // Track form submission attempt
  const trackFormSubmitAttempt = (formType: 'demo' | 'contact_sales', hasOptionalFields: boolean) => {
    trackDemoEvent('form_submit_attempt', {
      form_type: formType,
      has_optional_fields: hasOptionalFields,
      event_label: formType === 'demo' ? 'Demo Form Submit' : 'Sales Form Submit'
    });
  };

  // Track successful form submission (lead generation)
  // sourceButton is optional only for call-site backwards compatibility --
  // always pass it in practice, otherwise the conversion event can't be
  // broken down by which CTA actually drove it (the whole point of tagging
  // each button with a distinct id in the first place).
  const trackFormSubmitSuccess = (formType: 'demo' | 'contact_sales', company?: string, sourceButton?: string) => {
    // Standard lead generation event
    trackEvent('generate_lead', {
      event_category: 'Lead Generation',
      currency: 'USD',
      value: formType === 'contact_sales' ? 10 : 5, // Higher value for sales leads
      lead_source: formType === 'demo' ? 'book_demo_modal' : 'contact_sales_modal',
      company_name: company,
      source_button: sourceButton
    });

    // Custom success event
    trackDemoEvent('form_submit_success', {
      form_type: formType,
      source_button: sourceButton,
      event_label: formType === 'demo' ? 'Demo Request Success' : 'Sales Inquiry Success'
    });
  };

  // Track Calendly view
  const trackCalendlyView = (afterForm: 'demo' | 'contact_sales') => {
    trackDemoEvent('view_calendly', {
      after_form: afterForm,
      event_label: 'Calendly Widget Viewed'
    });
  };

  // Track Calendly scheduling (if we can detect it)
  const trackCalendlyScheduled = () => {
    trackDemoEvent('schedule_demo', {
      method: 'calendly',
      value: 20, // High value for completed scheduling
      event_label: 'Demo Scheduled'
    });

    // Also track as conversion
    trackEvent('conversion', {
      event_category: 'Lead Generation',
      event_label: 'Demo Scheduled',
      value: 20
    });
  };

  // Track form field interaction
  const trackFieldInteraction = (fieldName: string, formType: 'demo' | 'contact_sales') => {
    trackDemoEvent('form_field_interaction', {
      field_name: fieldName,
      form_type: formType,
      event_label: `${fieldName} field interaction`
    });
  };

  // Track form abandonment
  const trackFormAbandonment = (formType: 'demo' | 'contact_sales', lastField?: string) => {
    trackDemoEvent('form_abandon', {
      form_type: formType,
      last_field: lastField,
      event_label: 'Form Abandoned'
    });
  };

  // Track error
  const trackFormError = (formType: 'demo' | 'contact_sales', errorMessage: string) => {
    trackDemoEvent('form_error', {
      form_type: formType,
      error_message: errorMessage,
      event_label: 'Form Error'
    });
  };

  return {
    trackEvent,
    trackDemoEvent,
    getGAClientId,
    getGASessionId,
    getUTMParams,
    trackModalOpen,
    trackFormStart,
    trackFormSubmitAttempt,
    trackFormSubmitSuccess,
    trackCalendlyView,
    trackCalendlyScheduled,
    trackFieldInteraction,
    trackFormAbandonment,
    trackFormError
  };
};

export default useGoogleAnalytics;